/**
 * ConfiguratorPro — Storefront Product Configurator
 * Reads cfg_group / cfg_option metaobjects embedded in the page as JSON.
 * Supports display types: buttons, swatches, dropdown, radio.
 * Each option has its own price modifier.
 */
(function () {
  "use strict";

  const CONTAINER_ID = "cfgpro-configurator";

  function formatPrice(amount, currency) {
    if (amount === 0) return "Included";
    const sign = amount > 0 ? "+" : "−";
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  }

  function formatTotal(amount, currency) {
    return `$${amount.toFixed(2)}`;
  }

  class ProductConfigurator {
    constructor(container) {
      this.container = container;
      this.productId = container.dataset.productId;
      this.variantId = container.dataset.variantId;
      this.basePrice = parseFloat(container.dataset.basePrice) || 0;
      this.currency = container.dataset.currency || "PKR";
      this.showBreakdown = container.dataset.showBreakdown !== "false";
      this.swatchSize = container.dataset.swatchSize || "medium";
      this.dummyVariantId = container.dataset.dummyVariantId || null;

      this.groups = [];
      this.selections = {}; // { groupId: optionId }

      this.init();
    }

    init() {
      const dataEl = document.getElementById("cfgpro-groups-data");
      if (!dataEl) { this.container.style.display = "none"; return; }

      let raw = [];
      try { raw = JSON.parse(dataEl.textContent || "[]"); } catch (e) {
        console.error("[ConfiguratorPro] JSON parse error:", e);
        this.container.style.display = "none";
        return;
      }

      // Normalise raw metaobject data into internal group format
      this.groups = raw.map(g => ({
        id: String(g.id),
        name: String(g.name || ""),
        dt: String(g.display_type || "buttons"),
        required: g.required === true || g.required === "true",
        opts: (g.options || []).map(o => ({
          id: String(o.id),
          name: String(o.name || ""),
          price: parseFloat(o.price) || 0,
          color: String(o.color_hex || ""),
          subGroup: String(o.sub_group || ""),
          image: o.image_url || o.image || null,
        }))
      }));

      if (this.groups.length === 0) {
        this.container.style.display = "none";
        return;
      }

      this.render();
      this.updateSelectedLabels();
      this.calculatePrice();
      this.interceptAddToCart();
    }

    /* ── Render ─────────────────────────────────────────────────────────── */

    render() {
      this.container.innerHTML = "";
      this.container.classList.add("cfgpro");

      this.groupsWrapper = document.createElement("div");
      this.groupsWrapper.className = "cfgpro-groups";

      for (const group of this.groups) {
        this.groupsWrapper.appendChild(this.renderGroup(group));
      }

      this.container.appendChild(this.groupsWrapper);

      this.priceEl = document.createElement("div");
      this.priceEl.className = "cfgpro-total";
      this.container.appendChild(this.priceEl);

      this.errorEl = document.createElement("div");
      this.errorEl.className = "cfgpro-error";
      this.errorEl.setAttribute("role", "alert");
      this.errorEl.style.display = "none";
      this.container.appendChild(this.errorEl);
    }

    renderGroup(group) {
      const wrap = document.createElement("div");
      wrap.className = "cfgpro-group";
      wrap.dataset.groupId = group.id;

      // Header: name + selected label + chevron (accordion toggle)
      const header = document.createElement("button");
      header.type = "button";
      header.className = "cfgpro-group__header";
      header.setAttribute("aria-expanded", "false");
      header.setAttribute("aria-controls", `cfgpro-body-${group.id}`);

      const headerLeft = document.createElement("span");
      headerLeft.className = "cfgpro-group__header-left";

      const nameEl = document.createElement("span");
      nameEl.className = "cfgpro-group__name";
      nameEl.textContent = group.name.split(' - ')[0];
      if (group.required) {
        const req = document.createElement("span");
        req.className = "cfgpro-group__required";
        req.textContent = " *";
        nameEl.appendChild(req);
      }

      const selectedLabel = document.createElement("span");
      selectedLabel.className = "cfgpro-group__selected";
      selectedLabel.dataset.selectedLabel = group.id;

      headerLeft.appendChild(nameEl);

      if (group.description) {
        const info = document.createElement("button");
        info.type = "button";
        info.className = "cfgpro-group__info";
        info.setAttribute("aria-label", `Info: ${group.name}`);
        info.title = group.description;
        info.innerHTML = `<svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="15" height="15"><circle cx="8" cy="8" r="7.5" stroke="currentColor" stroke-width="1" fill="none"/><text x="8" y="12" text-anchor="middle" font-size="10" font-family="sans-serif" font-weight="bold">?</text></svg>`;
        info.addEventListener("click", e => {
          e.stopPropagation();
          alert(group.description);
        });
        headerLeft.appendChild(info);
      }

      const headerRight = document.createElement("span");
      headerRight.className = "cfgpro-group__header-right";

      // Small swatch preview shown when an option is selected
      const previewSwatch = document.createElement("span");
      previewSwatch.className = "cfgpro-group__preview";
      previewSwatch.dataset.previewSwatch = group.id;
      previewSwatch.style.display = "none";
      previewSwatch.addEventListener("click", (e) => {
        e.stopPropagation();
        const selId = this.selections[group.id];
        const opt = selId ? group.opts.find(o => o.id === selId) : null;
        if (opt) this.openLightbox(opt);
      });

      headerRight.appendChild(previewSwatch);
      headerRight.appendChild(selectedLabel);

      const chevron = document.createElement("span");
      chevron.className = "cfgpro-group__chevron";
      chevron.setAttribute("aria-hidden", "true");
      headerRight.appendChild(chevron);

      header.appendChild(headerLeft);
      header.appendChild(headerRight);
      wrap.appendChild(header);

      // Collapsible body
      const body = document.createElement("div");
      body.className = "cfgpro-group__body";
      body.id = `cfgpro-body-${group.id}`;
      body.hidden = true;

      header.addEventListener("click", () => {
        const isOpen = !body.hidden;
        body.hidden = isOpen;
        header.setAttribute("aria-expanded", String(!isOpen));
        wrap.classList.toggle("cfgpro-group--open", !isOpen);
      });

      // Options area
      const optsEl = document.createElement("div");

      if (group.dt === "dropdown") {
        optsEl.className = "cfgpro-options cfgpro-options--dropdown";
        optsEl.appendChild(this.renderDropdown(group));
      } else if (group.dt === "radio") {
        optsEl.className = "cfgpro-options cfgpro-options--radio";
        for (const opt of group.opts) optsEl.appendChild(this.renderRadio(group, opt));
      } else {
        // swatches or buttons — support sub-group headings
        const isSwatchType = group.dt === "swatches" || group.dt === "swatch_circle" || group.dt === "swatch_square";
        const renderFn = isSwatchType
          ? (opt) => this.renderSwatch(group, opt)
          : (opt) => this.renderButton(group, opt);
        const typeClass = isSwatchType ? group.dt : "buttons";

        const hasSubGroups = group.opts.some(o => o.subGroup);

        if (hasSubGroups) {
          optsEl.className = "cfgpro-options-subgroups";
          // Preserve original order while grouping
          const seen = [];
          const buckets = {};
          for (const opt of group.opts) {
            const key = opt.subGroup || "";
            if (!buckets[key]) { buckets[key] = []; seen.push(key); }
            buckets[key].push(opt);
          }
          for (const key of seen) {
            const section = document.createElement("div");
            section.className = "cfgpro-subgroup";
            if (key) {
              const heading = document.createElement("div");
              heading.className = "cfgpro-subgroup__heading";
              heading.textContent = key;
              section.appendChild(heading);
            }
            const row = document.createElement("div");
            row.className = `cfgpro-options cfgpro-options--${typeClass}`;
            for (const opt of buckets[key]) row.appendChild(renderFn(opt));
            section.appendChild(row);
            optsEl.appendChild(section);
          }
        } else {
          optsEl.className = `cfgpro-options cfgpro-options--${typeClass}`;
          for (const opt of group.opts) optsEl.appendChild(renderFn(opt));
        }
      }

      body.appendChild(optsEl);
      wrap.appendChild(body);
      return wrap;
    }

    renderDropdown(group) {
      const sel = document.createElement("select");
      sel.className = "cfgpro-select";
      sel.setAttribute("aria-label", group.name);

      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Select an option";
      placeholder.disabled = true;
      placeholder.selected = true;
      sel.appendChild(placeholder);

      for (const opt of group.opts) {
        const o = document.createElement("option");
        o.value = opt.id;
        o.textContent = opt.name + (opt.price !== 0 ? ` (${formatPrice(opt.price, this.currency)})` : " (Included)");
        sel.appendChild(o);
      }

      sel.addEventListener("change", () => {
        this.selections[group.id] = sel.value;
        this.updateSelectedLabels();
        this.calculatePrice();
        this.clearError();
      });
      return sel;
    }

    renderRadio(group, opt) {
      const label = document.createElement("label");
      label.className = "cfgpro-radio";

      const input = document.createElement("input");
      input.type = "radio";
      input.name = `cfgpro-${group.id}`;
      input.value = opt.id;
      input.className = "cfgpro-radio__input";
      if (this.selections[group.id] === opt.id) input.checked = true;

      input.addEventListener("change", () => {
        if (input.checked) {
          this.selections[group.id] = opt.id;
          this.updateSelectedLabels();
          this.calculatePrice();
          this.clearError();
        }
      });

      const nameSpan = document.createElement("span");
      nameSpan.className = "cfgpro-radio__name";
      nameSpan.textContent = opt.name;

      const priceSpan = document.createElement("span");
      priceSpan.className = "cfgpro-radio__price";
      priceSpan.textContent = formatPrice(opt.price, this.currency);

      label.appendChild(input);
      label.appendChild(nameSpan);
      label.appendChild(priceSpan);
      return label;
    }

    renderSwatch(group, opt) {
      const wrap = document.createElement("div");
      wrap.className = "cfgpro-swatch-wrap";
      wrap.dataset.optId = opt.id;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cfgpro-swatch";
      btn.dataset.optId = opt.id;
      btn.setAttribute("aria-label", `${opt.name}${opt.price !== 0 ? " " + formatPrice(opt.price, this.currency) : ""}`);
      if (this.selections[group.id] === opt.id) {
        btn.classList.add("cfgpro-swatch--selected");
        wrap.classList.add("cfgpro-swatch-wrap--selected");
      }

      const circle = document.createElement("span");
      circle.className = `cfgpro-swatch__color cfgpro-swatch__color--${this.swatchSize}`;
      const thumbnail = opt.image.replace(
        /\.(jpg|jpeg|png|webp)(\?.*)?$/,
        "_50x50.$1$2"
      );
      if (opt.image) {
        circle.style.backgroundImage = `url(${thumbnail})`;
        circle.style.backgroundSize = "cover";
        circle.style.backgroundPosition = "center";
      } else if (opt.color) {
        circle.style.backgroundColor = opt.color;
      }

      // Checkmark overlay (visible when selected)
      const check = document.createElement("span");
      check.className = "cfgpro-swatch__check";
      check.setAttribute("aria-hidden", "true");
      check.innerHTML = `<svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5l3.5 3.5L11 1" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      circle.appendChild(check);
      btn.appendChild(circle);
      wrap.appendChild(btn);

      // Label below — only shown for selected
      const label = document.createElement("span");
      label.className = "cfgpro-swatch__label";
      label.textContent = opt.name;
      wrap.appendChild(label);

      btn.addEventListener("click", () => {
        this.selections[group.id] = opt.id;
        this.updateSelectedStates(group);
        this.updateSelectedLabels();
        this.calculatePrice();
        this.clearError();
      });

      return wrap;
    }

    renderButton(group, opt) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cfgpro-btn";
      btn.dataset.optId = opt.id;
      if (this.selections[group.id] === opt.id) btn.classList.add("cfgpro-btn--selected");

      const nameEl = document.createElement("span");
      nameEl.className = "cfgpro-btn__name";
      nameEl.textContent = opt.name;
      btn.appendChild(nameEl);

      if (opt.price !== 0) {
        const pr = document.createElement("span");
        pr.className = "cfgpro-btn__price";
        pr.textContent = formatPrice(opt.price, this.currency);
        btn.appendChild(pr);
      }

      btn.addEventListener("click", () => {
        this.selections[group.id] = opt.id;
        this.updateSelectedStates(group);
        this.updateSelectedLabels();
        this.calculatePrice();
        this.clearError();
      });

      return btn;
    }

    /* ── State updates ──────────────────────────────────────────────────── */

    updateSelectedStates(group) {
      const groupEl = this.groupsWrapper.querySelector(`[data-group-id="${group.id}"]`);
      if (!groupEl) return;
      const selId = this.selections[group.id];
      groupEl.querySelectorAll(".cfgpro-swatch").forEach(el => {
        el.classList.toggle("cfgpro-swatch--selected", el.dataset.optId === selId);
      });
      groupEl.querySelectorAll(".cfgpro-swatch-wrap").forEach(el => {
        el.classList.toggle("cfgpro-swatch-wrap--selected", el.dataset.optId === selId);
      });
      groupEl.querySelectorAll(".cfgpro-btn").forEach(el => {
        el.classList.toggle("cfgpro-btn--selected", el.dataset.optId === selId);
      });
    }

    updateSelectedLabels() {
      for (const group of this.groups) {
        const labelEl = this.groupsWrapper.querySelector(`[data-selected-label="${group.id}"]`);
        const previewEl = this.groupsWrapper.querySelector(`[data-preview-swatch="${group.id}"]`);
        if (!labelEl) continue;

        const selId = this.selections[group.id];
        const opt = selId ? group.opts.find(o => o.id === selId) : null;

        if (opt) {
          const priceStr = opt.price !== 0 ? ` ${formatPrice(opt.price, this.currency)}` : "";
          labelEl.textContent = `— ${opt.name}${priceStr}`;
        } else {
          labelEl.textContent = "";
        }

        if (previewEl) {
          if (opt && (opt.image || opt.color)) {
            previewEl.style.display = "inline-block";
            if (opt.image) {
              previewEl.style.backgroundImage = `url(${opt.image})`;
              previewEl.style.backgroundSize = "cover";
              previewEl.style.backgroundPosition = "center";
              previewEl.style.backgroundColor = "";
            } else {
              previewEl.style.backgroundImage = "";
              previewEl.style.backgroundColor = opt.color;
            }
          } else {
            previewEl.style.display = "none";
          }
        }
      }
    }

    /* ── Pricing ────────────────────────────────────────────────────────── */

    calculatePrice() {
      let modifier = 0;
      for (const group of this.groups) {
        const selId = this.selections[group.id];
        if (!selId) continue;
        const opt = group.opts.find(o => o.id === selId);
        if (opt) modifier += opt.price;
      }

      const total = Math.max(0, this.basePrice + modifier);
      this.updatePriceDisplay(total);
      this.injectProperties(modifier);
    }

    updatePriceDisplay(total) {
      const formatted = formatTotal(total, this.currency);

      // Update theme price elements
      const selectors = [
        '.price-item--regular',
        '.product__price',
        '[data-product-price]',
        '.price__regular .price-item',
        '.product-single__price',
        '.price-item'
      ];
      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          if (el.closest('form[action*="/cart/add"], .product__info-container, .product-single, .product')) {
            el.innerHTML = formatted;
          }
        });
      });

      // Update our own total display
      if (this.priceEl) {
        this.priceEl.innerHTML = `<span class="cfgpro-total__label">Total:</span> <span class="cfgpro-total__amount">${formatted}</span>`;
      }
    }

    /* ── Cart injection ─────────────────────────────────────────────────── */

    injectProperties(totalModifier) {
      const form = this.container.closest("form") ||
        document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      form.querySelectorAll(".cfgpro-injected").forEach(el => el.remove());

      // One property per group showing selected option name
      for (const group of this.groups) {
        const selId = this.selections[group.id];
        if (!selId) continue;
        const opt = group.opts.find(o => o.id === selId);
        if (!opt) continue;

        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = `properties[${group.name}]`;
        inp.value = opt.name + (opt.price !== 0 ? ` (${formatPrice(opt.price, this.currency)})` : "");
        inp.className = "cfgpro-injected";
        form.appendChild(inp);
      }

      // Price modifier for Cart Transform function
      const modInp = document.createElement("input");
      modInp.type = "hidden";
      modInp.name = "properties[_price_modifier]";
      modInp.value = (totalModifier || 0).toFixed(2);
      modInp.className = "cfgpro-injected";
      form.appendChild(modInp);

      // Dummy variant for Cart Transform
      if (this.dummyVariantId) {
        const rawId = String(this.dummyVariantId).replace(/\D/g, "");
        if (rawId) {
          const dInp = document.createElement("input");
          dInp.type = "hidden";
          dInp.name = "properties[_dummy_variant_id]";
          dInp.value = `gid://shopify/ProductVariant/${rawId}`;
          dInp.className = "cfgpro-injected";
          form.appendChild(dInp);
        }
      }
    }

    /* ── Add-to-cart ────────────────────────────────────────────────────── */

    interceptAddToCart() {
      const form = this.container.closest("form") ||
        document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      document.addEventListener("click", (e) => {
        const btn = e.target.closest('button[name="add"], button[type="submit"]');
        if (!btn || btn.closest("form") !== form) return;

        const missing = this.groups.filter(g => g.required && !this.selections[g.id]);
        if (missing.length > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.showError(missing);
          return;
        }

        if (this.groups.length > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.submitCart(form);
        }
      }, true);
    }

    async submitCart(form) {
      const formData = new FormData(form);
      const mainId = formData.get("id") || document.querySelector('input[name="id"]')?.value || this.variantId;
      const mainQty = parseInt(formData.get("quantity") || 1, 10);

      const properties = {};
      let totalModifier = 0;

      for (const group of this.groups) {
        const selId = this.selections[group.id];
        if (!selId) continue;
        const opt = group.opts.find(o => o.id === selId);
        if (!opt) continue;
        properties[group.name] = opt.name + (opt.price !== 0 ? ` (${formatPrice(opt.price, this.currency)})` : "");
        totalModifier += opt.price;
      }

      properties["_price_modifier"] = totalModifier.toFixed(2);

      if (this.dummyVariantId) {
        const rawId = String(this.dummyVariantId).replace(/\D/g, "");
        if (rawId) properties["_dummy_variant_id"] = `gid://shopify/ProductVariant/${rawId}`;
      }

      const root = (window.Shopify?.routes?.root) || "/";
      const submitBtn = form.querySelector('[type="submit"], button[name="add"]');
      const origText = submitBtn?.innerText || "Add to cart";

      if (submitBtn) { submitBtn.disabled = true; submitBtn.innerText = "Adding…"; }

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: mainId, quantity: mainQty, properties }] })
        });
        if (res.ok) {
          window.location.href = `${root}cart`;
        } else {
          const err = await res.json();
          alert("Failed to add to cart: " + (err.description || "Unknown error"));
        }
      } catch (err) {
        alert("Error: " + err.message);
      } finally {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.innerText = origText; }
      }
    }

    openLightbox(opt) {
      const overlay = document.createElement("div");
      overlay.className = "cfgpro-lightbox";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");

      const close = () => overlay.remove();
      overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
      document.addEventListener("keydown", function handler(e) {
        if (e.key === "Escape") { close(); document.removeEventListener("keydown", handler); }
      });

      const inner = document.createElement("div");
      inner.className = "cfgpro-lightbox__inner";

      if (opt.image) {
        const img = document.createElement("img");
        img.src = opt.image;
        img.alt = opt.name;
        img.className = "cfgpro-lightbox__img";
        inner.appendChild(img);
      } else if (opt.color) {
        const swatch = document.createElement("div");
        swatch.className = "cfgpro-lightbox__swatch";
        swatch.style.backgroundColor = opt.color;
        inner.appendChild(swatch);
      }

      const info = document.createElement("div");
      info.className = "cfgpro-lightbox__info";
      info.innerHTML = `<span class="cfgpro-lightbox__name">${opt.name}</span>${opt.price !== 0 ? `<span class="cfgpro-lightbox__price">${formatPrice(opt.price, this.currency)}</span>` : ""}`;
      inner.appendChild(info);

      const closeBtn = document.createElement("button");
      closeBtn.className = "cfgpro-lightbox__close";
      closeBtn.setAttribute("aria-label", "Close");
      closeBtn.innerHTML = "×";
      closeBtn.addEventListener("click", close);
      inner.appendChild(closeBtn);

      overlay.appendChild(inner);
      document.body.appendChild(overlay);
    }

    showError(missingGroups) {
      const names = missingGroups.map(g => g.name).join(", ");
      this.errorEl.textContent = `Please select: ${names}`;
      this.errorEl.style.display = "";
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => this.clearError(), 5000);
    }

    clearError() {
      if (this.errorEl) this.errorEl.style.display = "none";
    }
  }

  function init() {
    const c = document.getElementById(CONTAINER_ID);
    if (c) new ProductConfigurator(c);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();



document.addEventListener("DOMContentLoaded", function () {

  const form = document.querySelector('form[action="/cart/add"]');

  form.addEventListener("submit", async function (e) {

    e.preventDefault();

    const formData = new FormData(form);

    const mainVariantId = formData.get("id");
    const mainQty = parseInt(formData.get("quantity")) || 1;

    // ADD PROPERTIES
    let properties = {};

    document.querySelectorAll('.cfgpro-group').forEach(group => {

      const groupName =
        group.querySelector('.cfgpro-group__name')?.innerText?.trim();

      const selected =
        group.querySelector('.cfgpro-group__selected')?.innerText?.trim();

      if (groupName && selected) {
        properties[groupName] = selected.replace(/^—\s*/, '');
      }

    });

    let items = [
      {
        id: mainVariantId,
        quantity: mainQty,
        properties
      }
    ];

    const selectedSpan = document.querySelector('.cfgpro-group__selected');

    if (selectedSpan) {

      const text = selectedSpan.innerText;

      // Extract price
      const match = text.match(/\+\$([\d,.]+)/);

      if (match) {

        // Quantity = price
        const addonQty = parseInt(
          match[1].replace(/,/g, '')
        );

        items = [
          {
            id: 48323134357718,
            quantity: addonQty
          },
          ...items
        ];

      }

    }

    try {

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      window.location.href = '/cart';

    } catch (error) {

      console.error(error);

    }

  });

});